import type {MouseEvent, ReactElement} from 'react';
import React, {useRef} from 'react';
import {getAssetUrl} from '@Utils/s3.util';
import {UploadButtonSection} from '@Panels/user-media-panel/components/upload-button-section';
import {openGooglePicker} from '@Libraries/google-picker';
import {openDropboxChooser} from '@Libraries/dropbox-chooser';
import {ClickableDiv} from '@Components/clickable-div';
import {toggleIsFbPanelOpenState} from '@Panels/user-media-panel/user-media-panel-reducer';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {openMoreOptionsModal} from '@Modals/more-options-modal';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import type {RECORD_DROPDOWN_OPTIONS} from '@Panels/user-media-panel/components/record-dropdown';
import {RecordDropdown} from '@Panels/user-media-panel/components/record-dropdown';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import type {ElementData, TempImageData} from '@Libraries/add-media-library';
import {isIOSApp, isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {openErrorModal} from '@Modals/error-modal';
import {getStateFromUrl} from '@Utils/url.util';
import {useAppDispatch} from '@/hooks';
import styles from './upload-and-social-media.module.scss';

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 660,
  },
  large: {
    minWidth: 661,
  },
};

interface UploadAndSocialMediaProps {
  uploadAcceptedMimetype?: string;
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  uploadVectors: boolean;
  defaultTab: UploadsMediaType;
  hideSocialMediaOptions?: boolean;
  mediaTypesToShow: Array<UploadsMediaType>;
  panelId?: string;
  hideRecordingOptions?: boolean;
  uploadSource?: MultiMediaUploadSource;
  maxFilesToUpload?: number;

  onClose(): void;

  onMediaUploaded(items?: Array<GridItemStorage | TempImageData>, currentUploadIndex?: number, totalUploadsCount?: number): void;
  onClickRecordOptions(id: RECORD_DROPDOWN_OPTIONS): void;
  onClickAiImage(e: MouseEvent): void;
  onClickAiVoice(e: MouseEvent): void;
  addSelectedItems(items?: Array<ElementData>): void;
  onStartAddSelected?: () => void;
  onUploadError?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
}

export function UploadAndSocialMedia({
  uploadSource = MultiMediaUploadSource.UPLOAD,
  onMediaUploaded = noop,
  uploadAcceptedMimetype = '',
  onClose = noop,
  addInBulk = false,
  shouldLoadDataUrlImage = false,
  hideSocialMediaOptions = false,
  defaultTab = UploadsMediaType.IMAGE,
  mediaTypesToShow = [],
  uploadVectors = true,
  hideRecordingOptions = false,
  onStartAddSelected = (): void => {},
  onUploadError = (): void => {},
  ...props
}: UploadAndSocialMediaProps): ReactElement | null {
  const oauthUrl = useRef('');
  const state = useRef('');
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const dispatch = useAppDispatch();

  const onAddSocialMediaItems = (items: Array<GridItemStorage | TempImageData>, currentUploadIndex?: number, totalUploadsCount?: number): void => {
    onMediaUploaded(items, currentUploadIndex, totalUploadsCount);
  };

  const closePanel = (): void => {
    onClose();
    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const getOauthUrl = async (): Promise<string> => {
    if (oauthUrl.current) {
      return oauthUrl.current;
    }
    oauthUrl.current = (await window.PMW.readLocal('authenticate/getCurrentUserGoogleToken', {
      isIOSAppOrEmbeddedEditor: isIOSAppOrEmbeddedEditor(),
    })) as string;
    if (!oauthUrl.current) {
      openErrorModal();
      throw new Error('Invalid oauthUrl');
    }
    state.current = getStateFromUrl(oauthUrl.current);
    return oauthUrl.current;
  };

  const onOpenGooglePhotoPicker = async (): Promise<void> => {
    const url = await getOauthUrl();
    void openGooglePicker(url, onAddSocialMediaItems, onStartAddSelected);
    closePanel();
  };

  const onOpenFacebookImageModal = (): void => {
    dispatch(toggleIsFbPanelOpenState(true));
    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const onOpenDropboxPhotoPicker = (): void => {
    void openDropboxChooser(onAddSocialMediaItems, onStartAddSelected, shouldLoadDataUrlImage);
    closePanel();
  };

  const getSocialMediaIcons = (): ReactElement | null => {
    if (hideSocialMediaOptions) {
      return null;
    }
    return (
      <div className={`flex-center flex-v-row spacing-p-l-2 ${styles.btnsContainer}`}>
        <ClickableDiv className={styles.socialThumbIcons} onClick={onOpenGooglePhotoPicker}>
          <img src={getAssetUrl(`google-drive-logo.${getCompatibleImageFileExtension('webp')}`)} alt={window.i18next.t('pmwjs_add_from_google_drive')} />
        </ClickableDiv>
        <ClickableDiv className={styles.socialThumbIcons} onClick={onOpenFacebookImageModal}>
          <img src={getAssetUrl(`facebook-logo-thumb-circle.${getCompatibleImageFileExtension('webp')}`)} alt={window.i18next.t('pmwjs_add_from_facebook')} />
        </ClickableDiv>
        <ClickableDiv className={styles.socialThumbIcons} onClick={onOpenDropboxPhotoPicker}>
          <img src={getAssetUrl(`dropbox-logo.${getCompatibleImageFileExtension('webp')}`)} alt={window.i18next.t('pmwjs_add_from_dropbox')} />
        </ClickableDiv>
      </div>
    );
  };

  const getSocialMediaListItems = (): ReactElement => {
    return (
      <>
        <ClickableDiv className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`} onClick={onOpenGooglePhotoPicker}>
          <img className={styles.socialThumbs} src={getAssetUrl('google-drive-logo.png')} alt={window.i18next.t('pmwjs_add_from_google_drive')} />
          <Text val={window.i18next.t('pmwjs_google_drive')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        <ClickableDiv className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`} onClick={onOpenFacebookImageModal}>
          <img className={styles.socialThumbs} src={getAssetUrl('facebook-logo-thumb-circle.png')} alt={window.i18next.t('pmwjs_add_from_facebook')} />
          <Text val={window.i18next.t('pmwjs_facebook')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        {isIOSApp() ? null : (
          <ClickableDiv className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`} onClick={onOpenDropboxPhotoPicker}>
            <img className={styles.socialThumbs} src={getAssetUrl('dropbox-logo.png')} alt={window.i18next.t('pmwjs_add_from_dropbox')} />
            <Text val={window.i18next.t('pmwjs_dropbox')} className="content-body" size={TextSize.XSMALL} />
          </ClickableDiv>
        )}
      </>
    );
  };

  const getRecordDropdown = (): ReactElement => {
    return (
      <RecordDropdown defaultTab={defaultTab} panelId={props.panelId} mediaTypesToShow={mediaTypesToShow} isSmallVersion onClickOption={onClickRecordOptions} onClose={onClose}>
        <ClickableDiv className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`} onClick={noop}>
          <Icon icon="icon-radio-button-checked" size={IconSize.SIZE_ICON_20} className={styles.recordIcon} />
          <Text val={window.i18next.t('pmwjs_record')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
      </RecordDropdown>
    );
  };

  const getAIImageOptionSelector = (): ReactElement => {
    return (
      <ClickableDiv
        className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`}
        onClick={(e) => {
          if (params.small) {
            dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
          }
          props.onClickAiImage(e);
        }}
      >
        <Icon icon="icon-ai-image" size={IconSize.SIZE_ICON_20} className={styles.aiIcon} />
        <Text val={window.i18next.t('pmwjs_ai_images')} className="content-body" size={TextSize.XSMALL} />
      </ClickableDiv>
    );
  };

  const getAIVoiceOptionSelector = (): ReactElement => {
    return (
      <ClickableDiv
        className={`flex-row-align-center spacing-p-3 spacing-m-b-4 radius-4 ${styles.socialMediaOption}`}
        onClick={(e) => {
          if (params.small) {
            dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
          }
          props.onClickAiVoice(e);
        }}
      >
        <Icon icon="icon-ai-voice" size={IconSize.SIZE_ICON_20} className={styles.aiIcon} />
        <Text val={window.i18next.t('pmwjs_ai_voice')} className="content-body" size={TextSize.XSMALL} />
      </ClickableDiv>
    );
  };

  const onClickRecordOptions = (id: RECORD_DROPDOWN_OPTIONS): void => {
    if (params.small) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
    props.onClickRecordOptions(id);
  };

  const getLargeScreenContent = (): ReactElement => {
    return (
      <>
        <UploadButtonSection
          onStartAddSelected={onStartAddSelected}
          uploadSource={uploadSource}
          hideRecordingOptions={hideRecordingOptions}
          panelId={props.panelId}
          uploadAcceptedMimetype={uploadAcceptedMimetype}
          onMediaUploaded={onMediaUploaded}
          onUploadError={onUploadError}
          addInBulk={addInBulk}
          shouldLoadDataUrlImage={shouldLoadDataUrlImage}
          onClose={onClose}
          uploadVectors={uploadVectors}
          defaultTab={defaultTab}
          mediaTypesToShow={mediaTypesToShow}
          onClickRecordOptions={onClickRecordOptions}
          maxFilesToUpload={props.maxFilesToUpload}
          addSelectedItems={(items) => {
            props.addSelectedItems(items);
          }}
          onClickAiImage={props.onClickAiImage.bind(null)}
          onClickAiVoice={props.onClickAiVoice.bind(null)}
        />
        {getSocialMediaIcons()}
      </>
    );
  };

  const getContentForMoreOptions = (): ReactElement => {
    return (
      <div>
        {hideSocialMediaOptions ? null : getSocialMediaListItems()}
        {getRecordDropdown()}
        {getAIImageOptionSelector()}
        {getAIVoiceOptionSelector()}
      </div>
    );
  };

  const openMoreOptions = (): void => {
    openMoreOptionsModal({
      className: 'spacing-p-4',
      headerTitle: window.i18next.t('pmwjs_import_media_from'),
      content: getContentForMoreOptions(),
    });
  };

  const getSmallScreenContent = (): ReactElement => {
    return (
      <div className="flex-v-row _full-width flexbox flex-row">
        <UploadButtonSection
          onStartAddSelected={onStartAddSelected}
          hideRecordingOptions={hideRecordingOptions}
          panelId={props.panelId}
          isSmallScreen
          btnClasses="flexbox flex-1"
          uploadAcceptedMimetype={uploadAcceptedMimetype}
          onMediaUploaded={onMediaUploaded}
          onUploadError={onUploadError}
          addInBulk={addInBulk}
          shouldLoadDataUrlImage={shouldLoadDataUrlImage}
          onClose={onClose}
          uploadVectors={uploadVectors}
          defaultTab={defaultTab}
          mediaTypesToShow={mediaTypesToShow}
          maxFilesToUpload={props.maxFilesToUpload}
          onClickRecordOptions={(id: RECORD_DROPDOWN_OPTIONS): void => {
            props.onClickRecordOptions(id);
          }}
          addSelectedItems={(items) => {
            props.addSelectedItems(items);
          }}
          onClickAiImage={noop}
          onClickAiVoice={noop}
        />
        <div>
          <Icon icon="icon-more-h" className="spacing-m-l-2" size={IconSize.SIZE_ICON_24} shape={IconShape.SQUARE} type={IconType.SECONDARY} onClick={openMoreOptions} />
        </div>
      </div>
    );
  };

  return (
    <div className={`flex-row-justify-center _full-width ${params.large ? styles.sectionContainer : ''}`} ref={containerRef}>
      {params.small ? getSmallScreenContent() : getLargeScreenContent()}
    </div>
  );
}
